import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';

import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Modal, Steps } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import { Formik, Form } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import type { ICardApiResponse } from 'v2/interfaces/customer';
import { customerFactory } from 'v2/services/customer';

import ListCards from './ListCards';
import NewCard from './newCard';
import PreviewCard from './previewCard';
import CardSchema from './validations';

export interface ICard {
  id?: number;
  name: string;
  cvc: string;
  expiry: string;
  number: string;
  issuer?: string;
}

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  onFinish?: (card: ICard) => void;
  cardsList: ICardApiResponse[];
  isLoadingCards: boolean;
  onCardClick?: (card: ICard) => void;
}

const steps = ['Selecione ou cadastre um novo cartão', 'Confirme seu cartão selecionado'];

export default function SelectCreditCard({ isOpen, onClose, onFinish, cardsList, isLoadingCards }: IProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [newCard, setNewCard] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<ICard>();
  const queryClient = useQueryClient();

  const handleNext = useCallback(() => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  }, []);

  const addNewCard = useCallback(() => {
    setNewCard(true);
  }, []);

  const backToList = useCallback(() => {
    setNewCard(false);
  }, []);

  const onClickCard = useCallback((card: ICard) => {
    setSelectedCard(card);
  }, []);

  const [onSubmit] = useCallbackObservable((values: ICard, { setSubmitting, resetForm }: FormikProps<ICard>) => {
    setSubmitting(true);
    return customerFactory.saveNewCard(values).pipe(
      tap(data => {
        const card: ICard = { ...values, ...{ id: data.id, number: data.number, issuer: data.flag } };
        setSelectedCard(card);
        setSubmitting(false);
        setNewCard(false);
        resetForm();
        toast.success('Forma de pagamento cadastrada com sucesso!');
        queryClient.invalidateQueries({ queryKey: ['paymentMethods', 'subscriptions'] });
      }),
      catchError(err => {
        setSubmitting(false);
        toast.error(err instanceof Error ? err.message : err);
        return of(true);
      })
    );
  }, []);

  const onComplete = useCallback(() => {
    onFinish(selectedCard);
    setActiveStep(0);
    setSelectedCard(null);
  }, [onFinish, selectedCard]);

  const onHandleClose = useCallback(() => {
    setActiveStep(0);
    setSelectedCard(null);
    onClose();
  }, [onClose]);

  return (
    <Formik
      validationSchema={CardSchema}
      initialValues={{ number: '', name: '', expiry: '', cvc: '' }}
      onSubmit={onSubmit}
    >
      {({ dirty, isValid, isSubmitting, submitForm }) => (
        <Form>
          <Modal
            open={isOpen}
            onCancel={onHandleClose}
            width={1100}
            style={{ maxWidth: 'calc(100vw - 16px)' }}
            centered
            destroyOnClose
            title={<span className='text-content-title text-lg font-medium'>Selecionar forma de pagamento</span>}
            footer={
              <div className='flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end [&>button]:w-full sm:[&>button]:w-auto'>
                {activeStep === 0 && newCard ? (
                  <>
                    <Button onClick={backToList} icon={<ArrowLeftOutlined />}>
                      Voltar
                    </Button>
                    <Button
                      onClick={submitForm}
                      disabled={!dirty || !isValid || isSubmitting}
                      loading={isSubmitting}
                      type='primary'
                      icon={<SaveOutlined />}
                      className='sm:min-w-30'
                    >
                      Salvar Novo Cartão
                    </Button>
                  </>
                ) : (
                  <>
                    {!newCard && activeStep === 0 && (
                      <Button type='dashed' onClick={addNewCard} icon={<PlusOutlined />}>
                        Adicionar novo cartão
                      </Button>
                    )}
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} icon={<ArrowLeftOutlined />}>
                        Voltar
                      </Button>
                    )}
                    <Button
                      disabled={activeStep === 0 && !selectedCard}
                      type='primary'
                      onClick={activeStep === steps.length - 1 ? onComplete : handleNext}
                      icon={activeStep === steps.length - 1 ? <CheckOutlined /> : <ArrowRightOutlined />}
                    >
                      {activeStep === steps.length - 1 ? 'Confirmar' : 'Avançar'}
                    </Button>
                  </>
                )}
              </div>
            }
          >
            <Steps current={activeStep} size='small' items={steps.map(s => ({ title: s }))} />
            <div className='py-4 sm:py-6'>
              {activeStep === 0 && !newCard && (
                <ListCards
                  cardsList={cardsList}
                  selectedCard={selectedCard}
                  onCardClick={onClickCard}
                  isLoadingCards={isLoadingCards}
                />
              )}
              {activeStep === 0 && newCard && <NewCard />}
              {activeStep === 1 && selectedCard && (
                <div className='py-2 text-center'>
                  <PreviewCard card={selectedCard} />
                </div>
              )}
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  );
}
